import {RuntimeVariable} from "../Dashboarding/Dashboard";
import React, {useEffect, useRef, useState} from "react";
import {TimeRange} from "../../types/time";
import {ChartStyling, ChartType, MetoroChart} from "./MetoroChart";
import {
    createMultiMetricRequest,
    GetMultiMetrics, MetoroQLQuerySpecifier,
    Metric,
    MetricSpecifier,
    MetricVisualization,
    SingleMetricRequest
} from "../../clients/metoro/metrics";
import {formatFilterValues} from "../Filter/Filter";
import {AggregationFunction, EvalType, MissingDatapointBehavior, WindowUnit} from "../../pages/alerts/MetricAlert";
import {useDebouncedCallback} from "use-debounce";
import {ChartComparisonDialog} from "../ChartComparisonDialog";
import {LoadingSpinner} from "../ui/customSpinner";
import isEqual from "lodash/isEqual";
import {MetricChartDisplaySettings, MultiMetricChartWidget} from "../Dashboarding/internalwidgets";
import {Formula} from "../../types/formula";
import {AlertType, ThresholdComparator} from "../../pages/alerts/model";
import {
    createAlertUrlFromAlert,
    createMetricExplorerUrlFromMetricChartProps
} from "../../pages/alerts/utils";
import {ApiServerAlert} from "../Alert/utils";
import {getAggregateStartEndTime} from "./StatChart";
import {environments} from "eslint-plugin-prettier";
import {useSearchParams} from "react-router-dom";

export interface MultiMetoroMetricsChartProps {
    // The epoch time in seconds of the start of the time range
    startTime: number;
    // The epoch time in seconds of the end of the time range
    endTime: number;
    // The title of the chart
    title?: string;
    // Whethere to allow setting the title from the chart, and what happens with it
    setTitle?: (title: string) => void;
    // The type of the chart
    type: ChartType;
    // Hide on no data
    hideOnNoData?: boolean;
    // Extra styling options for the chart
    styling?: ChartStyling;
    // Class name overrides for the chart container on the inside
    className?: string;
    // threshold if defined
    threshold?: Threshold;
    // threshold label if defined
    thresholdLabel?: string;
    // highlight time with vertical line annotations
    timePeriodHighlight?: TimePeriod;
    // hide the legend
    hideLegend?: boolean;
    // Any variables that are in scope, this will be used to substitute the variables in the filters
    variables?: RuntimeVariable[];
    setTimeRange?: React.Dispatch<React.SetStateAction<TimeRange>>;
    // The actual metrics to show on the chart
    metricSpecifiers: MetricSpecifier[];
    // Formulas to apply to the metrics
    formulas?: Formula[];
    // MetoroQLQueries
    metoroQLQueries?: MetoroQLQuerySpecifier[];
    // If it's an aggregate selector then we need to change the datapoints we consider
    isAggregateSelector?: boolean
    // Custom colours for the metrics
    customColours?: Map<Record<string, string>, string[]>
    // Flag to indicate if using MetoroQL mode
    isMetoroQlSelector?: boolean
    //
    displaySettings?: MetricChartDisplaySettings;
}

function isMulti(obj: any): obj is MultiMetoroMetricsChartProps {
    return obj["metricSpecifiers"] !== undefined;
}

export interface MathExpression {
    variables: string[];
    expression: string;
}

// A variable in a math expression
interface MathExpressionVariable {
    // variable name, e.g. a, b, c, etc.
    name: string;
    // value is the name of the metric this variable represents
    // e.g. a / 60 where a = requests_total metric.
    value: string;
}

interface ColorPairing {
    backgroundColor: string;
    borderColor: string;
}

const colorings: ColorPairing[] = [
    {
        borderColor: "#3793ff",
        backgroundColor: "#3793ff33",
    },
    {
        borderColor: "#ff6384",
        backgroundColor: "#ff638433",
    },
    {
        borderColor: "#ffce56",
        backgroundColor: "#ffce5633",
    },
    {
        borderColor: "#4bc0c0",
        backgroundColor: "#4bc0c033",
    },
    {
        borderColor: "#ff9f40",
        backgroundColor: "#ff9f4033",
    },
    {
        borderColor: "#ff66ff",
        backgroundColor: "#ff66ff33",
    },
    {
        borderColor: "#9c27b0",
        backgroundColor: "#9c27b033",
    },
    {
        borderColor: "#4caf50",
        backgroundColor: "#4caf5033",
    },
    {
        borderColor: "#795548",
        backgroundColor: "#79554833",
    },
    {
        borderColor: "#607d8b",
        backgroundColor: "#607d8b33",
    },
    {
        borderColor: "#e91e63",
        backgroundColor: "#e91e6333",
    },
    {
        borderColor: "#009688",
        backgroundColor: "#00968833",
    },
    {
        borderColor: "#39ff14",
        backgroundColor: "#39ff1433",
    },
    {
        borderColor: "#fe019a",
        backgroundColor: "#fe019a33",
    },
    {
        borderColor: "#0ff0fc",
        backgroundColor: "#0ff0fc33",
    },
    {
        borderColor: "#ccff00",
        backgroundColor: "#ccff0033",
    }
]

function MetricsToChartData(metrics: Metric[] | undefined, colorBackground: string | undefined = undefined, colorBorder: string | undefined = undefined, customColours: Map<Record<string, string>, string[]> | undefined = undefined): any {
    if (metrics === undefined) {
        return undefined;
    }
    let data = [];
    let i = 0;
    for (let metric of metrics) {
        // Add null check for timeSeries before sorting
        if (!metric.timeSeries) {
            continue;
        }

        // Sorted by total value of the time series
        let sorted = metric.timeSeries.sort((a, b) => {
            let totalA = a.data.reduce((acc, curr) => {
                return acc + curr.value
            }, 0)
            let totalB = b.data.reduce((acc, curr) => {
                return acc + curr.value
            }, 0)
            return totalB - totalA
        })

        for (let timeSeries of sorted) {
            let label = metric.metricVisualization?.displayName ? metric.metricVisualization.displayName + " " : ""
            const entries = Object.entries(timeSeries.attributes)
            let customColourBackground = undefined;
            let customColourBorder = undefined;
            if (customColours !== undefined) {
                for (let [attributePair, colour] of customColours) {
                    for (let entry of entries) {
                        if (entry[0] === attributePair.key && entry[1] === attributePair.value) {
                            customColourBackground = colour[1];
                            customColourBorder = colour[0];
                            break;
                        }
                    }
                }
            }

            let addedEntries = entries.length > 0
            for (const entry of entries) {
                label += entry[0] + "=" + formatFilterValues(entry[0], entry[1] as string) + ", ";
            }
            if (addedEntries) {
                label = label.substring(0, label.length - 2);
            }
            let dataset = {
                label: label,
                data: timeSeries.data.map((dataPoint) => {
                    return {
                        x: dataPoint.time,
                        y: dataPoint.value
                    }
                }),
                borderWidth: 1,
                // borderSkipped: "middle",
                borderDash: metric.metricVisualization?.lineDash,
                pointRadius: metric.metricVisualization?.lineDotSize,
                backgroundColor:
                    metric.metricVisualization?.lineDotColor !== undefined ? metric.metricVisualization.lineDotColor :
                        customColourBackground !== undefined ? customColourBackground : colorBackground ? colorBackground : colorings[i % colorings.length].backgroundColor,
                borderColor:
                    metric.metricVisualization?.lineColor !== undefined ? metric.metricVisualization.lineColor :
                        customColourBorder !== undefined ? customColourBorder : colorBorder ? colorBorder : colorings[i % colorings.length].borderColor,
                // Add unit information to the dataset
                unit: metric.metricVisualization?.unit || metric.unit
            };
            data.push(dataset);
            i++;
        }
    }
    return {
        datasets: data,
    };
}

export interface AppearanceProps {
    // If not set, its shown. If set to true, it hides the selector
    hideGroupBySelector?: boolean;
    hideFilterSelector?: boolean;
    hideMetricSelector?: boolean;
    hideAggregationSelector?: boolean;
}

export interface TimePeriod {
    start: number;
    end: number;
}

export interface Threshold {
    value: string;
    comparator?: ThresholdComparator;
}

interface MultiMetricRequest {
    metrics: SingleMetricRequest[];
    formulas?: Formula[];
}

async function updateChartMetrics(props: MultiMetoroMetricsChartProps,
                                  showAll: boolean,
                                  setMetric: (value: (((prevState: (Metric[] | undefined)) => (Metric[] | undefined)) | Metric[] | undefined)) => void,
                                  setResultsLimited: (value: (((prevState: boolean) => boolean) | boolean)) => void,
                                  setActualResultLen: (value: (((prevState: number) => number) | number)) => void,
                                  abortController: AbortController,
                                  setAbortController: React.Dispatch<React.SetStateAction<AbortController>>,
                                  variables: RuntimeVariable[],
                                  setError: (value: string | null) => void,
                                  setIsLoading: (value: boolean) => void,
                                  environments?: string[] | undefined,
) {
    let startTime = props.startTime;
    let endTime = props.endTime

    if (props.isAggregateSelector) {
        let res = getAggregateStartEndTime(props.metricSpecifiers, startTime, endTime)
        startTime = res[0]
        endTime = res[1]
    }

    const request = createMultiMetricRequest(
        props.metricSpecifiers,
        startTime,
        endTime,
        showAll,
        props.formulas,
        variables,
        props.metoroQLQueries,
        environments,
    );

    try {
        setIsLoading(true);
        const awaitedMetrics = await GetMultiMetrics({...request},
            abortController,
            setAbortController);

        // Check for errors in responses
        const errorsFound = awaitedMetrics.metrics
            .filter(metric => metric.error)
            .map(metric => metric.error);

        if (errorsFound.length > 0) {
            // Set the error to display in the chart
            setError(errorsFound.join('\n'));
            return;
        }

        // Clear any previous errors if successful
        setError(null);

        let numNotRemoved = props.metricSpecifiers.filter(x => !(x.shouldNotReturn == true)).length

        // Define the type for formula specifiers
        type FormulaSpecifier = {
            visualization?: MetricVisualization;
            displayName: string;
            isFormula: true;
        };

        // Create a combined array of metric specifiers, formulas, and MetoroQL queries
        const combinedSpecifiers: (MetricSpecifier | FormulaSpecifier | MetoroQLQuerySpecifier)[] = [
            ...props.metricSpecifiers.filter(x => !(x.shouldNotReturn == true)),
            ...(props.formulas?.map(formula => ({
                visualization: formula.visualization,
                displayName: formula.visualization?.displayName || formula.formula,
                isFormula: true as const
            })) || []),
            ...(props.metoroQLQueries?.filter(q => !(q.shouldNotReturn)) || [])
        ];

        let metrics = awaitedMetrics.metrics
            .map((metricX, index) => {
                // Find the appropriate specifier based on index
                let specifier;
                if (index < combinedSpecifiers.length) {
                    specifier = combinedSpecifiers[index];
                }

                const isFormulaResult = metricX.metric?.name === "formula";
                // Check if this is a MetoroQL result based on response properties
                const isMetoroQLResult = typeof metricX.type === 'string' && metricX.type.toLowerCase() === "metoroql";

                let displayName = `M${index + 1}`;

                // Determine display name based on result type
                if (isFormulaResult) {
                    displayName = specifier && 'isFormula' in specifier ? 
                        (specifier.displayName || `F${index + 1}`) : `F${index + 1}`;
                } else if (isMetoroQLResult) {
                    displayName = `QL ${index + 1}`;
                } else if (metricX.formulaIdentifier && metricX.formulaIdentifier !== "") {
                    displayName = metricX.formulaIdentifier;
                }

                return {
                    ...metricX,
                    metric: metricX.metric !== undefined ? {
                        ...metricX.metric,
                        // Override unit if specified in visualization
                        unit: specifier?.visualization?.unit ?? metricX.metric.unit,
                        metricVisualization: specifier?.visualization ? {
                            ...specifier.visualization,
                            displayName: specifier.visualization.displayName || displayName
                        } : {
                            displayName: displayName
                        }
                    } : undefined
                }
            })
            .filter((metric) => metric !== undefined && !metric.error)
            .map((metric) => metric.metric!)
        let isResultLimited = awaitedMetrics.metrics.map(x => x.isResultLimited).reduce((a, b) => a || b) || false
        let resultLen = awaitedMetrics.metrics.filter(x => x.isResultLimited !== undefined).map(x => x.resultLen!).reduce((a, b) => a + b);

        setMetric(metrics);
        setResultsLimited(isResultLimited)
        setActualResultLen(resultLen);
        setIsLoading(false);
    } catch (error: any) {
        // Don't show errors for canceled requests
        if (error.name === "AbortError" || 
            error.code === "ECONNABORTED" ||
            (error.message && (
                error.message.includes("canceled") || 
                error.message.includes("cancelled") ||
                error.message.includes("abort")
            ))) {
            console.log("Request was cancelled");
            return;
        }
        setIsLoading(false);

        // Handle API errors including MetoroQL parsing errors
        console.error("Error fetching metrics:", error);

        // Check if it's an HTTP error with a response
        if (error.response && error.response.data && error.response.data.error) {
            setError(error.response.data.error);
        } else {
            // Generic error handling
            setError(error.message || "An unknown error occurred");
        }
    }
}

const usePrevious = (value: any, initialValue: any) => {
    const ref = useRef(initialValue);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
};

const useEffectDebugger = (effectHook: any, dependencies: any, dependencyNames: any[] = []) => {
    const previousDeps = usePrevious(dependencies, []);

    const changedDeps = dependencies.reduce((accum: any, dependency: any, index: any) => {
        if (dependency !== previousDeps[index]) {
            const keyName = dependencyNames[index] || index;
            return {
                ...accum,
                [keyName]: {
                    before: previousDeps[index],
                    after: dependency
                }
            };
        }

        return accum;
    }, {});

    if (Object.keys(changedDeps).length) {
        console.log('[use-effect-debugger] ', changedDeps);
    }

    useEffect(effectHook, dependencies);
};

function createMetricChartWidgetFromMetricChartProps(props: MultiMetoroMetricsChartProps): MultiMetricChartWidget {
    return {
        position: {
            x: undefined,
            y: undefined,
            w: 6,
            h: 3,
        },
        metricSpecifiers: props.metricSpecifiers,
        title: props.title,
        widgetType: 'MetricChart',
        type: props.type,
        formulas: props.formulas,
        displaySettings: props.displaySettings
    };
}

function safeMapToObject<T>(map: Map<string, T> | undefined): Record<string, T> {
    if (!map || !(map instanceof Map)) {
        return {};
    }
    return Object.fromEntries(map);
}

function createAlertFromMetricChartProps(props: MultiMetoroMetricsChartProps): ApiServerAlert {
    return {
        destinations: [],
        uuid: "",
        name: "Monitor for " + (props.title || "Multiple Metrics"),
        description: "Describe why you want to monitor these metrics",
        type: AlertType.MultiMetric,
        muted: false,
        multiMetricAlert: {
            filters: {
                metricSpecifiers: props.metricSpecifiers.map(spec => ({
                    metricType: spec.metricType,
                    metricName: spec.metricName,
                    filters: spec.filters,
                    excludeFilters: spec.excludeFilters,
                    regexes: spec.regexes || [],
                    excludeRegexes: spec.excludeRegexes || [],
                    splits: spec.splits || [],
                    aggregation: spec.aggregation,
                    bucketSize: spec.bucketSize || 0,
                    functions: spec.functions || [],
                    jsonPath: spec.jsonPath,
                    shouldNotReturn: spec.shouldNotReturn || false
                })),
                formula: props.formulas && props.formulas.length > 0 ? props.formulas[0] : undefined
            },
            monitorEvaluation: {
                description: "Eval",
                monitorEvaluationType: EvalType.Static,
                monitorEvalutionPayload: {
                    evaluationFunction: AggregationFunction.Sum,
                    window: 1,
                    windowUnit: WindowUnit.Minutes,
                    evaluationSplits: [],
                    evaluationWindow: 1,
                    datapointsToAlarm: 1,
                    missingDatapointBehavior: MissingDatapointBehavior.NotBreaching
                }
            },
            alarmCondition: {
                condition: ThresholdComparator.GreaterThan,
                threshold: "0"
            }
        }
    };
}

function MultiMetoroMetricsChart(props: MultiMetoroMetricsChartProps) {
    const [metrics, setMetrics] = React.useState<Metric[]>();
    const [resultsLimited, setResultsLimited] = React.useState<boolean>(false);
    const [actualResultLen, setActualResultLen] = React.useState<number>(0);
    const [showAll, setShowAll] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [environments, setEnvironments] = useState<string[]>([]);
    const [searchParams] = useSearchParams();
    const debouncedUpdateChartMetrics = useDebouncedCallback(
        async (
            props: MultiMetoroMetricsChartProps,
            showAll: boolean,
            setMetrics: (value: (((prevState: (Metric[] | undefined)) => (Metric[] | undefined)) | Metric[] | undefined)) => void,
            setResultsLimited: (value: (((prevState: boolean) => boolean) | boolean)) => void,
            setActualResultLen: (value: (((prevState: number) => number) | number)) => void,
            abortController: AbortController,
            setAbortController: React.Dispatch<React.SetStateAction<AbortController>>,
            variables: RuntimeVariable[],
            setIsLoading: (value: boolean) => void,
            setError: (value: string | null) => void,
            environments?: string[] | undefined
        ) => {
            try {
                // Clear any previous errors
                setError(null);
                await updateChartMetrics(props, showAll, setMetrics, setResultsLimited, setActualResultLen, abortController, setAbortController, variables, setError, setIsLoading, environments);
            } catch (e) {
                console.error(e);
            }
        },
        50
    );
    const [updateChartMetricsAbortController, setUpdateChartMetricsAbortController] = useState<AbortController>(new AbortController());
    const alert = createAlertFromMetricChartProps(props);
    const alertUrl = createAlertUrlFromAlert(alert)
    const metricExplorterUrl = createMetricExplorerUrlFromMetricChartProps(props);
    const [chartComparisonDialogOpen, setComparisonDialogOpen] = useState(false);

    useEffect(() => {
        let environment = searchParams.get("environment");
        if (environment !== null && environment !== "") {
            setEnvironments([environment]);
        } else {
            setEnvironments([]);
        }
    }, [searchParams]);

    const [propsState, setPropsState] = useState<MultiMetoroMetricsChartProps>(props);
    useEffect(() => {
        if (!isEqual(props, propsState)) {
            setPropsState(props);
        }
    }, [props]);

    let annotations: any[] = [];

    function getLineStyleForThreshold(threshold: Threshold): any {
        if (threshold.comparator != undefined && threshold.comparator == ThresholdComparator.GreaterThan || threshold.comparator == ThresholdComparator.LessThan) {
            return [5, 5]
        } else {
            return [];
        }
    }

    if (propsState.threshold != undefined && propsState.threshold.value != undefined) {
        // single line on the threshold
        annotations.push({
            type: 'line',
            mode: 'horizontal',
            yMin: propsState.threshold.value,
            yMax: propsState.threshold.value,
            borderColor: 'rgb(239 68 68)',
            borderWidth: 2,
            borderDash: getLineStyleForThreshold(propsState.threshold),
        });
        // shaded area on the threshold
        if (propsState.threshold.comparator != undefined) {
            if (propsState.threshold.comparator == ThresholdComparator.GreaterThan || propsState.threshold.comparator == ThresholdComparator.GreaterThanOrEqual) {
                if (props.thresholdLabel && props.thresholdLabel != "") {
                    annotations.push({
                        type: 'label',
                        backgroundColor: 'rgba(255, 99, 132, 0.1)',
                        borderWidth: 0,
                        content: [props.thresholdLabel],
                        color: "rgb(239 68 68)",
                        yValue: propsState.threshold.value,
                        position: "end"
                    })
                }
                annotations.push({
                    type: 'box',
                    yMin: propsState.threshold.value,
                    backgroundColor: 'rgba(255, 99, 132, 0.1)',
                    borderWidth: 0
                })
            } else if (propsState.threshold.comparator == ThresholdComparator.LessThan || propsState.threshold.comparator == ThresholdComparator.LessThanOrEqual) {
                if (props.thresholdLabel && props.thresholdLabel != "") {
                    annotations.push({
                        type: 'label',
                        backgroundColor: 'rgba(255, 99, 132, 0.1)',
                        borderWidth: 0,
                        content: [props.thresholdLabel],
                        color: "rgb(239 68 68)",
                        yValue: propsState.threshold.value,
                        position: "end"
                    })
                }
                annotations.push({
                    type: 'box',
                    yMax: propsState.threshold.value,
                    backgroundColor: 'rgba(255, 99, 132, 0.1)',
                    borderWidth: 0
                })
            }
        }
    } else if (props.timePeriodHighlight != undefined && !Number.isNaN(props.timePeriodHighlight.start) && !Number.isNaN(props.timePeriodHighlight.end)) {
        annotations.push({
            type: 'line',
            mode: 'vertical',
            xMin: props.timePeriodHighlight.start * 1000,
            xMax: props.timePeriodHighlight.start * 1000,
            borderColor: 'rgb(239 68 68)',
            borderWidth: 2,
            borderDash: [],
        });
        annotations.push({
            type: 'line',
            mode: 'vertical',
            xMin: props.timePeriodHighlight.end * 1000,
            xMax: props.timePeriodHighlight.end * 1000,
            borderColor: 'rgb(239 68 68)',
            borderWidth: 2,
            borderDash: [],
        });
        annotations.push({
            type: 'box',
            xMin: props.timePeriodHighlight.start * 1000,
            xMax: props.timePeriodHighlight.end * 1000,
            backgroundColor: 'rgba(255, 99, 132, 0.1)',
            borderWidth: 0,
        })
    }

    useEffect(() => {
        setShowAll(false)
    }, [propsState.metricSpecifiers]);

    useEffect(() => {
        setIsLoading(true);
        debouncedUpdateChartMetrics(
            propsState,
            showAll,
            setMetrics,
            setResultsLimited,
            setActualResultLen,
            updateChartMetricsAbortController,
            setUpdateChartMetricsAbortController,
            props.variables || [],
            setIsLoading,
            setError,
            environments
        );
    }, [
        propsState.metricSpecifiers, 
        propsState.startTime, 
        propsState.endTime, 
        propsState.type, 
        propsState.title, 
        showAll, 
        props.variables, 
        propsState.formulas,
        propsState.metoroQLQueries,
        propsState.isMetoroQlSelector
    ]);
    const dataToUse = MetricsToChartData(metrics, undefined, undefined, props.customColours);

    // Determine the unit to display for the chart
    const getChartUnit = (): string => {
        if (!metrics || metrics.length === 0) {
            return '';
        }
        
        // Get all unique units from the metrics, considering visualization overrides
        // and treating "1" (dimensionless) as empty
        const units = metrics
            .map(metric => {
                // Check if there's a visualization unit override
                const vizUnit = metric.metricVisualization?.unit;
                return vizUnit || metric.unit || '';
            })
            .map(unit => unit === '1' ? '' : unit) // Treat dimensionless as empty
            .filter(unit => unit.trim() !== '');
        
        // If all metrics have the same unit (or all are empty), use that unit
        const uniqueUnits = [...new Set(units)];
        if (uniqueUnits.length <= 1) {
            return uniqueUnits[0] || '';
        }
        
        // If metrics have different units, don't show any unit
        return '';
    };

    const chartUnit = getChartUnit();

    if ((dataToUse === undefined || dataToUse.datasets === undefined || dataToUse.datasets.length === 0) && props.hideOnNoData && !error) {
        return <></>
    }

    const metricChartWidget = createMetricChartWidgetFromMetricChartProps(propsState)

    return (
        <div className={"flex grow shrink relative max-w-full"}>
            <ChartComparisonDialog
                isOpen={chartComparisonDialogOpen}
                setOpen={setComparisonDialogOpen}
                chartProps={propsState}/>
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-50">
                    <LoadingSpinner size={32}/>
                </div>
            )}
            <MetoroChart
                setTitle={props.setTitle}
                setTimeRange={props.setTimeRange}
                setComparisonDialogOpen={setComparisonDialogOpen}
                metricChartWidget={metricChartWidget}
                createAlertURL={alertUrl}
                metricExplorerURL={metricExplorterUrl}
                className={propsState.className} hideOnNoData={propsState.hideOnNoData}
                styling={propsState.styling}
                type={propsState.type} dataToUse={dataToUse} title={propsState.title} annotations={annotations}
                hideLegend={propsState.hideLegend} resultsLimited={resultsLimited}
                actualResultLen={actualResultLen} showAll={showAll} setShowAll={setShowAll}
                metricUnit={chartUnit}/>

            {error && (
                <div className="absolute inset-0 flex items-center justify-center z-40">
                    <div className={`bg-red-500/30 dark:bg-red-900/70 border border-red-500 dark:border-red-800 rounded-md p-4 m-4 max-w-[80%] shadow-lg backdrop-blur-sm ${
                        !dataToUse || !dataToUse.datasets || dataToUse.datasets.length === 0 ? 'bg-opacity-95 dark:bg-opacity-95' : 'bg-opacity-90 dark:bg-opacity-70'
                    }`}>
                        <div className="flex items-start">
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-textlight dark:text-red-200 font-medium">Query Error</h3>
                                <div className="mt-2 text-xs text-textmedium dark:text-red-300 max-h-32 overflow-auto">
                                    {error.split('\n').map((line, i) => (
                                        <p key={i} className="mb-1">{line}</p>
                                    ))}
                                </div>
                                <div className="mt-3 text-xs text-textmedium dark:text-red-400 font-medium">
                                    Please check your query syntax.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export {
    MetricsToChartData,
    MultiMetoroMetricsChart,
    isMulti
};

export type {MetricSpecifier};
